// Admin Dashboard
// - 列出所有用戶 + 今日 / 本月 NTD 花費
// - 新增用戶
// - 即時調整每日預算 / 重設密碼 / 停用

import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

interface AdminUser {
  id: number;
  username: string | null;
  email: string;
  display_name: string | null;
  is_admin: number;
  status: string;
  daily_budget_twd: number;
  workspace_id: number | null;
  today_cost_twd: number;
  month_cost_twd: number;
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  async function load() {
    setLoadingUsers(true);
    try {
      const res = await fetch("/api/admin/users", { credentials: "same-origin" });
      const data = (await res.json()) as { users: AdminUser[] };
      setUsers(data.users ?? []);
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    if (user?.is_admin) void load();
  }, [user]);

  if (loading) {
    return <div className="p-8 text-sm text-brand-dark/60">載入中⋯⋯</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="p-8 text-sm text-brand-dark">
        這個頁面只有管理員可看。
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl font-bold text-brand-dark">
            用戶管理
          </h1>
          <p className="text-xs text-brand-dark/60 mt-1">
            即時查看每位用戶的 API 花費，並可調整每日預算上限
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-brand-dark text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-brand-dark/90"
        >
          新增用戶
        </button>
      </div>

      {loadingUsers ? (
        <div className="text-sm text-brand-dark/60">載入用戶⋯⋯</div>
      ) : (
        <div className="bg-white border border-brand-gray/15 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-cream/60 text-xs uppercase tracking-wider text-brand-dark/60">
              <tr>
                <th className="px-4 py-3 text-left">帳號</th>
                <th className="px-4 py-3 text-left">名稱</th>
                <th className="px-4 py-3 text-left">狀態</th>
                <th className="px-4 py-3 text-right">今日 NT$</th>
                <th className="px-4 py-3 text-right">本月 NT$</th>
                <th className="px-4 py-3 text-right">每日預算</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const overBudget =
                  u.daily_budget_twd > 0 && u.today_cost_twd >= u.daily_budget_twd;
                return (
                  <tr
                    key={u.id}
                    className="border-t border-brand-gray/10 hover:bg-brand-cream/30"
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      {u.username ?? `id-${u.id}`}
                      {u.is_admin === 1 && (
                        <span className="ml-2 inline-block text-[10px] bg-brand-gold/20 text-brand-gold px-1.5 py-0.5 rounded">
                          ADMIN
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{u.display_name ?? "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {u.status === "active" ? (
                        <span className="text-green-700">啟用中</span>
                      ) : (
                        <span className="text-red-700">已停用</span>
                      )}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono ${
                        overBudget ? "text-red-700 font-bold" : ""
                      }`}
                    >
                      {u.today_cost_twd.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-brand-dark/70">
                      {u.month_cost_twd.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {u.daily_budget_twd > 0 ? (
                        <>NT${u.daily_budget_twd}</>
                      ) : (
                        <span className="text-brand-dark/40">不設限</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditingId(u.id)}
                        className="text-xs text-brand-gold hover:underline"
                      >
                        調整
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-brand-dark/50 leading-relaxed">
        <strong>NTD 換算：</strong>USD × 32（可在 wrangler.toml 設 TWD_PER_USD 覆寫）。
        <br />
        <strong>計費來源：</strong>Claude API（input / output / cache）+ Workers AI Whisper。
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}

      {editingId !== null && (
        <EditUserModal
          user={users.find((u) => u.id === editingId)!}
          isCurrentUser={editingId === user.id}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [budget, setBudget] = useState(50);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          username,
          password,
          display_name: displayName || username,
          daily_budget_twd: budget,
        }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "建立失敗");
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-lg p-6 w-full max-w-md space-y-4 shadow-xl"
      >
        <h2 className="font-serif font-bold text-lg text-brand-dark">
          新增用戶
        </h2>

        <Field label="帳號（英數字 ≥ 3）">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            minLength={3}
            pattern="[a-zA-Z0-9_-]+"
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
          />
        </Field>
        <Field label="密碼（≥ 8 字）">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
          />
        </Field>
        <Field label="顯示名稱">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
            placeholder="預設同帳號"
          />
        </Field>
        <Field label="每日預算（NT$；0 = 不設限）">
          <input
            type="number"
            min={0}
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
          />
        </Field>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-brand-dark/70 hover:text-brand-dark"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-brand-dark text-white px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "建立中⋯⋯" : "建立"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({
  user,
  isCurrentUser,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  isCurrentUser: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [budget, setBudget] = useState(user.daily_budget_twd);
  const [status, setStatus] = useState(user.status);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const payload: Record<string, unknown> = {};
    if (budget !== user.daily_budget_twd) payload.daily_budget_twd = budget;
    if (status !== user.status) payload.status = status;
    if (newPassword) payload.password = newPassword;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "儲存失敗");
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <form
        onSubmit={save}
        className="bg-white rounded-lg p-6 w-full max-w-md space-y-4 shadow-xl"
      >
        <div>
          <h2 className="font-serif font-bold text-lg text-brand-dark">
            調整用戶設定
          </h2>
          <div className="text-xs text-brand-dark/60 mt-1">
            {user.username} · {user.display_name}
          </div>
        </div>

        <Field label="每日預算（NT$；0 = 不設限）">
          <input
            type="number"
            min={0}
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
          />
          <div className="text-xs text-brand-dark/50 mt-1">
            今日已用 NT${user.today_cost_twd.toFixed(2)} / 本月 NT$
            {user.month_cost_twd.toFixed(2)}
          </div>
        </Field>

        <Field label="狀態">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={isCurrentUser}
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm bg-white"
          >
            <option value="active">啟用中</option>
            <option value="suspended">停用</option>
          </select>
          {isCurrentUser && (
            <div className="text-xs text-brand-dark/50 mt-1">
              不能停用自己
            </div>
          )}
        </Field>

        <Field label="重設密碼（留空則不變）">
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={8}
            placeholder="≥ 8 字"
            className="w-full px-3 py-2 border border-brand-gray/30 rounded-md text-sm"
          />
        </Field>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-brand-dark/70 hover:text-brand-dark"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-brand-dark text-white px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "儲存中⋯⋯" : "儲存"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-brand-dark/70 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
